import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'application/store';

import { AccountState } from './types';
import { ACCOUNT_FETCH_CONFIG } from './consts';

export const selectAccountState = (state: RootState): AccountState => state.account;

export const selectLastFetchTime = (state: RootState) => state.account.lastFetchTime;

export const selectAccountLoading = createSelector([selectAccountState], (account) => account.loading);

export const selectAccountError = createSelector([selectAccountState], (account) => account.error);

export const selectUserData = createSelector([selectAccountState], (account) => account.userData);

export const selectUserId = createSelector([selectUserData], (userData) => userData?.id ?? '');

export const selectCompanyIds = createSelector(
  [selectUserData],
  (userData) => userData?.companies?.map((c) => c.id) ?? []
);

export const selectUserDataEmail = createSelector([selectUserData], (userData) => userData?.email);

export const selectUserDataPhone = createSelector([selectUserData], (userData) => userData?.phone);

export const selectUserDataFirstName = createSelector([selectUserData], (userData) => userData?.firstName);

export const selectUserDataSmartLkAccess = createSelector([selectUserData], (userData) => userData?.smartLkAccess);

export const selectUserDataRegistrationSource = createSelector(
  [selectUserData],
  (userData) => userData?.registrationSource
);

export const selectUserDataMainCompany = createSelector([selectUserData], (userData) => userData?.mainCompany);

export const selectUserDataMainCompanyCountry = createSelector(
  [selectUserDataMainCompany],
  (mainCompany) => mainCompany?.country
);

export const selectUserDataPointsExists = createSelector([selectUserData], (userData) => userData?.pointsExists);

export const selectUserDataIsTest = createSelector([selectUserData], (userData) => userData?.isTest);

export const selectUserDataPersonalAccountAccess = createSelector(
  [selectUserData],
  (userData) => userData?.personalAccountAccess
);

export const selectCurrentCompanyId = createSelector([selectAccountState], (account) => account.currentCompanyId);

export const selectStreamLibraryId = createSelector([selectAccountState], (account) => account.streamLibraryId);

export const selectIsBillingEnabled = createSelector([selectAccountState], (account) => account.isBillingEnabled);

export const selectIsReadyToUseLK = createSelector([selectAccountState], (account) => account.isReadyToUseLK);

export const selectIsAuthorized = createSelector([selectAccountState], (account) => account.isAuthorized);

export const selectIsWithoutCompanies = createSelector([selectAccountState], (account) => account.isWithoutCompanies);

export const selectHasActiveContractMigration = createSelector(
  [selectAccountState],
  (account) => account.hasActiveContractMigration
);

export const selectAccountDataStale = createSelector([selectAccountState], (account) => account.dataStale);

export const selectAccountStatus = createSelector(
  [selectAccountLoading, selectAccountError, selectIsAuthorized],
  (loading, error, isAuthorized) => ({
    loading,
    error,
    isAuthorized,
    hasError: !!error,
    isIdle: !loading && !error && !isAuthorized,
  })
);

export const selectShouldRefreshData = createSelector([selectAccountState], (account) => {
  if (account.dataStale) return true;

  if (!account.lastFetchTime) return true;

  const timeSinceLastFetch = Date.now() - account.lastFetchTime;

  return timeSinceLastFetch > ACCOUNT_FETCH_CONFIG.staleTime;
});

export const selectAccountData = createSelector(
  [
    selectUserId,
    selectCurrentCompanyId,
    selectCompanyIds,
    selectIsReadyToUseLK,
    selectIsAuthorized,
    selectAccountLoading,
    selectAccountError,
  ],
  (userId, currentCompanyId, companyIds, isReadyToUseLK, isAuthorized, loading, error) => ({
    userId,
    currentCompanyId,
    companyIds,
    isReadyToUseLK,
    isAuthorized,
    loading,
    error,
  })
);
